// thumbnailUtils.js

/**
 * Generate a thumbnail for various file types
 * @param {File} file - The file to generate a thumbnail for
 * @param {Object} options - Configuration options
 * @param {number} options.maxSize - Maximum thumbnail dimension in pixels (default: 150)
 * @param {number} options.quality - JPEG quality for image/video thumbnails (0-1, default: 0.7)
 * @returns {Promise<string>} - Base64 encoded thumbnail image
 */
export const generateThumbnail = async (file, options = {}) => {
    const {
        maxSize = 150,
        quality = 0.7
    } = options;

    return new Promise((resolve) => {
        // Helper to get file extension
        const getFileExtension = (filename) => {
            return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
        };

        // Helper to create icon thumbnail for non-media files
        const createIconThumbnail = (text) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = maxSize;
            canvas.height = maxSize;
            
            // Draw background
            ctx.fillStyle = '#f3f4f6';
            ctx.fillRect(0, 0, maxSize, maxSize);
            
            // Draw file type text
            ctx.fillStyle = '#4b5563';
            ctx.font = '14px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(text.toUpperCase(), maxSize / 2, maxSize / 2);
            
            return canvas.toDataURL('image/png');
        };

        // Check if it's a 3D model or DXF file
        const modelExtensions = ['glb', 'gltf', 'obj', 'fbx', 'stl', '3ds', 'dxf'];
        const fileExtension = getFileExtension(file.name);
        
        if (modelExtensions.includes(fileExtension)) {
            resolve(createIconThumbnail(fileExtension));
            return;
        }

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    let width = img.width;
                    let height = img.height;
                    
                    // Calculate dimensions while maintaining aspect ratio
                    if (width > height) {
                        if (width > maxSize) {
                            height = height * (maxSize / width);
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width = width * (maxSize / height);
                            height = maxSize;
                        }
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL(file.type, quality));
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);

        } else if (file.type.startsWith('video/')) {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            video.autoplay = false;
            video.muted = true;
            video.playsInline = true;

            const handleVideoLoad = () => {
                return new Promise((resolveLoad) => {
                    video.addEventListener('loadeddata', () => resolveLoad(), { once: true });
                    video.addEventListener('error', () => resolveLoad(), { once: true });
                });
            };

            video.onloadedmetadata = async () => {
                try {
                    await handleVideoLoad();
                    
                    let width = video.videoWidth;
                    let height = video.videoHeight;
                    
                    if (width > height) {
                        if (width > maxSize) {
                            height = height * (maxSize / width);
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width = width * (maxSize / height);
                            height = maxSize;
                        }
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    
                    // Capture frame at 1 second or at the start for shorter videos
                    video.currentTime = Math.min(1, video.duration);
                    
                    await new Promise(resolve => {
                        video.onseeked = () => {
                            ctx.drawImage(video, 0, 0, width, height);
                            resolve();
                        };
                    });
                    
                    resolve(canvas.toDataURL('image/jpeg', quality));
                } finally {
                    video.remove();
                    canvas.remove();
                }
            };

            const reader = new FileReader();
            reader.onloadend = () => {
                video.src = reader.result;
            };
            reader.readAsDataURL(file);

        } else {
            // For other file types, create an icon with the file type
            let fileType;
            if (file.type) {
                fileType = file.type.split('/')[1]?.toUpperCase() || 'FILE';
            } else {
                fileType = getFileExtension(file.name).toUpperCase() || 'FILE';
            }
            
            resolve(createIconThumbnail(fileType));
        }
    });
};

// Example usage:
/*
// Basic usage with defaults
const thumbnail = await generateThumbnail(file);

// Custom options
const thumbnail = await generateThumbnail(file, {
    maxSize: 200,
    quality: 0.8
});
*/